import { useQuery } from '@tanstack/vue-query'
import { type ComputedRef, computed } from 'vue'

import type { CompanyRequestsResponse } from '@/modules/companies/interfaces/companyRequest'

import { getHotelReport } from '../services/hotelReportApi'

interface Params {
	companyId?: number
	page: number
	perPage: number
	filters: {
		dates?: string[] | null
		creationDates?: string[] | null
		status?: string
		travelerId?: number
		hotelId?: number
		createdAt?: string[] | null
		orderBy?: string
		orderDirection?: 'asc' | 'desc'
	}
}

/** Reporting is separately authorized; operational reservation lists stay unchanged. */
export function useHotelReportReservations(params: ComputedRef<Params>) {
	const enabled = computed(() => !!params.value.companyId)
	const query = useQuery({
		queryKey: computed(() => ['hotelReportReservations', params.value]),
		enabled,
		queryFn: async ({ signal }) => {
			const { companyId, page, perPage, filters } = params.value
			const { data } = await getHotelReport<CompanyRequestsResponse>(
				companyId!,
				'reservations',
				{
					signal,
					params: {
						page,
						perPage,
						checkOutDates: filters.dates,
						createdDate: filters.creationDates,
						status: filters.status,
						traveler: filters.travelerId,
						hotel: filters.hotelId,
						createdAt: filters.createdAt,
						sortBy: filters.orderBy,
						sortDirection: filters.orderDirection,
					},
				}
			)
			return data
		},
		staleTime: 0,
		cacheTime: 0,
		retry: false,
		refetchOnWindowFocus: false,
	})
	return {
		requests: computed(() => query.data.value?.data ?? []),
		meta: computed(() => query.data.value?.meta),
		isLoading: computed(() => enabled.value && query.isFetching.value),
	}
}
